'use client'

import { useContext, useEffect, useMemo, useState } from 'react'

import cn from 'clsx'
import type { Themes } from '@/Theme'

import { useThrottledResize } from '@/utils/useThrottledResize'

import { ThemeContext } from '@/utils/themeContext'
import { IconButton } from '@/components/IconButton/IconButton'
import { X } from '@doist/icons/brand'
import { Close } from '@doist/icons/base'

import css from './VideoLightBox.module.css'

export type VideoLightBoxProps = {
    title: string
    youtubeVideoId: string
    closeLabel: string
    onClose: () => void
    width: number
    height: number
    lang: string
    playerVars?: YT.PlayerVars
}

const defaultPlayerVars: YT.PlayerVars = {
    modestbranding: 1,
    rel: 0,
    autoplay: 1,
}

export function VideoLightBox({
    title,
    youtubeVideoId,
    width = 1143,
    height = 643,
    closeLabel = 'Close banner',
    onClose,
    lang = 'en',
    playerVars = {},
}: VideoLightBoxProps) {
    const { theme } = useContext(ThemeContext)

    const [videoSizeStyles, setVideoStyles] = useState({
        width: `${width}px`,
        height: `${height}px`,
    })

    const youtubeURL = useMemo(() => {
        playerVars.hl = playerVars.hl ?? lang
        const embedURL = new URL(`https://www.youtube.com/embed/${youtubeVideoId}`)
        const urlQuery = new URLSearchParams({ ...defaultPlayerVars, ...playerVars } as Record<
            string,
            string
        >)
        embedURL.search = urlQuery.toString()
        return embedURL.toString()
    }, [playerVars, youtubeVideoId, lang])

    /** Disable scrolling */
    useEffect(() => {
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [])

    /** Resize video and keep aspect ratio */
    const ratio = height / width
    useThrottledResize(() => {
        const checkForResize = () => {
            const windowWidth = window.innerWidth
            const windowHeight = window.innerHeight
            if (width < windowWidth && height < windowHeight) {
                setVideoStyles({
                    width: `${width}px`,
                    height: `${height}px`,
                })
            } else {
                const resolvedWidth = Math.round(windowWidth * 0.8)
                const resolvedHeight = resolvedWidth * ratio
                setVideoStyles({
                    width: `${resolvedWidth}px`,
                    height: `${resolvedHeight}px`,
                })
            }
        }
        checkForResize()
        return checkForResize
    }, [])

    return (
        <div className={css.videoLightBox}>
            <div className={css.overlay} onClick={onClose} onKeyDown={onClose} />
            <div className={css.videoContainer} style={videoSizeStyles}>
                <iframe
                    title={title}
                    src={youtubeURL}
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
                {theme !== 'twist' ? (
                    <CloseButton theme={theme} aria-label={closeLabel} onClick={onClose} />
                ) : undefined}
            </div>
            {theme === 'twist' ? (
                <CloseButton theme={theme} aria-label={closeLabel} onClick={onClose} />
            ) : undefined}
        </div>
    )
}

function CloseButton({
    theme,
    ...props
}: {
    theme: Themes
    'aria-label': string
    onClick: () => void
}) {
    return (
        <IconButton
            className={cn(css.closeBtn, { [css.twistCloseBtn]: theme === 'twist' })}
            icon={theme === 'twist' ? X : Close}
            size={theme === 'twist' ? '24' : '48'}
            radius={theme === 'twist' ? undefined : '50%'}
            variant={theme === 'twist' ? 'ghost' : undefined}
            {...props}
        />
    )
}
